import { Inject, Injectable } from '@angular/core';
import { ListSalespersonsRequestListSalespersonsFiltersInterface, SalespersonService } from '@vendasta/sales';
import { map, Observable, of, take } from 'rxjs';
import { IAMService, User, UserIdentifierInterface } from '@vendasta/iam';
import { switchMap } from 'rxjs/operators';
import { ADDITIONAL_SALESPEOPLE, ASSIGNED_SALESPERSON, getFormattedDisplayName } from './users.service';
import { TranslateService } from '@ngx-translate/core';
import {
  AutomationReplacementChipsDataService,
  PagedResponse,
} from '../common/components/automation-replacement-chips/interfaces';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';

@Injectable({ providedIn: 'root' })
export class SalespersonSelectorService implements AutomationReplacementChipsDataService<User> {
  supportsSearching = true;

  private assignedSalesperson = new User({
    userId: ASSIGNED_SALESPERSON,
    firstName: this.translateService.instant('AUTOMATIONS.COMMON.ASSIGNED_SALESPERSON'),
    lastName: '',
  });
  private additionalSalespeople = new User({
    userId: ADDITIONAL_SALESPEOPLE,
    firstName: this.translateService.instant('AUTOMATIONS.COMMON.ADDITIONAL_SALESPEOPLE'),
    lastName: '',
  });
  private additionalAssignees: User[] = [this.assignedSalesperson, this.additionalSalespeople];

  constructor(
    private readonly salespersonService: SalespersonService,
    private readonly iamService: IAMService,
    private readonly translateService: TranslateService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
  ) {}

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    shouldNotAddAdditionalAssignees: boolean,
  ): Observable<PagedResponse<User>> {
    return this.namespace$.pipe(
      switchMap((partnerId) => {
        // If the user only has on market then filter to that, if they have access to multiple markets then we
        // are making the assumption that they have access to all markets.
        const filter: ListSalespersonsRequestListSalespersonsFiltersInterface = {
          partnerId: partnerId,
          searchTerm: searchTerm,
        };
        return this.salespersonService
          .listSalespersons(filter, cursor, pageSize)
          .pipe(map((resp) => ({ resp, cursor })));
      }),
      take(1),
      map(({ resp, cursor }) => {
        let userIds = [];
        const nextCursor = resp.nextCursor;
        const hasMore = resp.hasMore;
        if (!resp.results) {
          return { userIds, cursor, nextCursor, hasMore };
        }
        userIds = resp.results.map((s) => ({ subjectId: s.salespersonId }) as UserIdentifierInterface);
        return { userIds, cursor, nextCursor, hasMore };
      }),
      switchMap(({ userIds, cursor, nextCursor, hasMore }) => {
        if (userIds.length === 0) {
          const users = [];
          return of({ users, nextCursor, hasMore });
        }
        return this.iamService.getMultiUsers(userIds).pipe(map((users) => ({ users, cursor, nextCursor, hasMore })));
      }),
      map(({ users, nextCursor, hasMore }) => {
        users = (users || []).filter((u) => u);
        if (!cursor && !searchTerm) {
          if (!shouldNotAddAdditionalAssignees) {
            users = this.additionalAssignees.concat(users);
          }
        }
        return {
          results: users,
          hasMore: hasMore,
          nextCursor: nextCursor,
        };
      }),
    );
  }

  convertDataToDisplay(s: User): Observable<string> {
    return of(getFormattedDisplayName(s) || '');
  }

  convertDataToFormValue(x: User): any {
    return x?.userId;
  }

  getMulti(x: string[]): Observable<User[]> {
    const assignedSalespersonIndex = (x || []).indexOf(ASSIGNED_SALESPERSON);
    const additionalSalespeopleIndex = (x || []).indexOf(ADDITIONAL_SALESPEOPLE);
    const userIds = (x || []).map((u) => ({ userId: u }) as UserIdentifierInterface);
    return this.iamService.getMultiUsers(userIds).pipe(
      map((users) => {
        if (assignedSalespersonIndex >= 0) {
          users[assignedSalespersonIndex] = this.assignedSalesperson;
        }
        if (additionalSalespeopleIndex >= 0) {
          users[additionalSalespeopleIndex] = this.additionalSalespeople;
        }
        return users;
      }),
    );
  }
}
